import {
    Box,
    Collapse,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    Popover,
    TextField,
    Typography,
} from '@mui/material'
import Markdown from 'markdown-to-jsx'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { menuHelpWidth } from 'router/layouts/AdminLayout'
import { IDocument } from 'hooks/useHelpDocument/useHelpDocument'
import SearchIcon from '@mui/icons-material/Search'
import { keyBy } from 'lodash-es'
import {
    defaultTitleDocument,
    useHelpDocumentContext,
} from './HelpDocumentContext'
interface DisplayDocument extends Pick<IDocument, 'title' | 'category' | 'id'> {
    hightlighTitle: string
    hightlightCategory: string
    hightlightTag: string
    hightlightContent: string
}

const BUFFER = 30
const OPEN_MARK_TAG =
    '<mark style="background:#10ac84 !important;color:white !important">'
const CLOSE_MARK_TAG = '</mark>'

const ResponsiveImage = ({ alt, src, title }: any) => (
    <img
        src={src}
        alt={alt}
        title={title}
        style={{
            maxWidth: '33vw',
            height: 'auto',
            display: 'block',
            margin: '0 auto',
        }}
    />
)
const highlightText = (
    text: string,
    highlighText: string,
    isSplitString: boolean = false,
) => {
    const regex = new RegExp(`(${highlighText})`, 'gi')
    const fullText = text.replace(regex, `${OPEN_MARK_TAG}$1${CLOSE_MARK_TAG}`)
    return isSplitString
        ? fullText.substring(
              fullText.indexOf(OPEN_MARK_TAG) - BUFFER < 0
                  ? 0
                  : fullText.indexOf(OPEN_MARK_TAG) - BUFFER,
              fullText.indexOf(CLOSE_MARK_TAG) + BUFFER,
          )
        : fullText
}

export default function HelpDocument() {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = useMemo(() => Boolean(anchorEl), [anchorEl])
    const id = useMemo(() => (open ? 'popover' : undefined), [open])
    const {
        allDocuments,
        currentDocument,
        docIndex,
        onSelectCurrentDocument,
        loadingContent,
        categoryDocuments,
        selectCategory,
    } = useHelpDocumentContext()

    const [openDocList, setOpenDocList] = useState(false)
    const [searchQuery, setSearchQuery] = useState<string>('')

    const [searchResults, setSearchResult] = useState<DisplayDocument[]>([])

    const displayDocs = useMemo(
        () => allDocuments.filter((d) => d.category !== 'help'),
        [allDocuments],
    )

    const onToggleDocList = useCallback(
        () => setOpenDocList((prev) => !prev),
        [],
    )
    const onLoadDocument = useCallback(
        (title: string) => {
            const selectDoc = allDocuments.find((i) => i.title === title)
            if (!selectDoc) return
            setOpenDocList(false)
            onSelectCurrentDocument(selectDoc)
        },
        [allDocuments, onSelectCurrentDocument],
    )

    const mapTagsToTagText = useCallback(
        (tags: string[]) => tags.map((i) => `#${i}`).join(' '),
        [],
    )

    const setAllSearchResults = useCallback(
        () =>
            setSearchResult(
                displayDocs.map((i) => ({
                    ...i,
                    hightlightTag: mapTagsToTagText(i.tags),
                    hightlightContent: '',
                    hightlightCategory: i.category,
                    hightlighTitle: i.title,
                })),
            ),
        [displayDocs, mapTagsToTagText],
    )

    const onSelectDocumentInSearch = useCallback(
        (title: string) => {
            onLoadDocument(title)
        },
        [onLoadDocument],
    )

    const onSearchChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value.trimStart()
            setSearchQuery(value ?? '')

            if (value === '') {
                setAllSearchResults()
                return
            }
            const results = docIndex?.search(value)
            const resultsMap = keyBy(results, 'ref')
            if (!results) return

            setSearchResult(
                displayDocs
                    .filter((d) => results.some((r) => Number(r.ref) === d.id))
                    .map<DisplayDocument>((d) => ({
                        ...d,
                        hightlighTitle: highlightText(d.title, value),
                        hightlightCategory: highlightText(d.category, value),
                        hightlightTag: highlightText(
                            mapTagsToTagText(d.tags),
                            value,
                        ),
                        hightlightContent: highlightText(
                            d.contentText,
                            value,
                            true,
                        ),
                    }))
                    .sort((a, b) =>
                        resultsMap[a.id].score < resultsMap[b.id].score
                            ? 1
                            : -1,
                    ),
            )
        },
        [docIndex, displayDocs, mapTagsToTagText, setAllSearchResults],
    )

    const onSearchFocus = useCallback(
        (event: React.FocusEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget)
        },
        [],
    )

    const onSearchBlur = useCallback(() => {
        setAnchorEl(null)
    }, [])

    useEffect(() => {
        setAllSearchResults()
    }, [setAllSearchResults])

    // To select one document of default `selectCategory` on first load
    useEffect(() => {
        if (allDocuments.length === 0) return

        onSelectCurrentDocument(
            currentDocument ??
                allDocuments.find(
                    (d) => d.title === defaultTitleDocument.get(selectCategory),
                )!!,
        )
    }, [allDocuments, currentDocument, onSelectCurrentDocument, selectCategory])

    return (
        <Grid container direction="column" marginTop={7.5}>
            <Box
                sx={{
                    backgroundColor: 'primary.main',
                    p: 1,
                    position: 'fixed',
                    width: menuHelpWidth,
                    zIndex: 99,
                }}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    InputProps={{ endAdornment: <SearchIcon /> }}
                    onFocus={onSearchFocus}
                    onBlur={onSearchBlur}
                    onChange={onSearchChange}
                    value={searchQuery}
                />
                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onSearchBlur}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    disableAutoFocus
                >
                    <Box
                        sx={{
                            width: '33vw',
                            minHeight: '30vh',
                            maxHeight: '70vh',
                        }}
                    >
                        {searchResults.length === 0 ? (
                            <Typography align="center" marginTop={6}>
                                No results
                            </Typography>
                        ) : (
                            searchResults.map((i, index) => (
                                <ListItemButton
                                    key={index}
                                    sx={{ padding: 2 }}
                                    onClick={() =>
                                        onSelectDocumentInSearch(i.title)
                                    }
                                    divider
                                >
                                    <ListItemText
                                        primary={
                                            <Box
                                                sx={{ flexDirection: 'column' }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '1.2em',
                                                        fontWeight: 600,
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            i.hightlighTitle,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: '1em',
                                                        fontWeight: 300,
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            i.hightlightCategory,
                                                    }}
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Box
                                                sx={{ flexDirection: 'column' }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: '1em',
                                                        fontWeight: 500,
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: i.hightlightTag,
                                                    }}
                                                />
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.9em',
                                                        fontWeight: 100,
                                                        pt: 2,
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html:
                                                            i.hightlightContent,
                                                    }}
                                                />
                                            </Box>
                                        }
                                    />
                                </ListItemButton>
                            ))
                        )}
                    </Box>
                </Popover>
            </Box>
            <Grid item marginTop={7.5}>
                <ListItemButton onClick={onToggleDocList} color="primary">
                    <ListItemText>📖 Docs</ListItemText>
                    {openDocList ? <ExpandLess /> : <ExpandMore />}
                </ListItemButton>

                <Collapse in={openDocList} timeout="auto" unmountOnExit>
                    <List
                        component="div"
                        disablePadding
                        sx={{
                            maxWidth: menuHelpWidth,
                            overflow: 'hidden',
                        }}
                    >
                        {categoryDocuments.map((doc, index) => (
                            <ListItemButton
                                key={index}
                                onClick={() => onLoadDocument(doc.title)}
                                selected={currentDocument?.title === doc.title}
                            >
                                <ListItemText>{doc.title}</ListItemText>
                            </ListItemButton>
                        ))}
                    </List>
                </Collapse>
            </Grid>

            <Grid item p={1}>
                {categoryDocuments.length === 0 ? (
                    <Box>
                        <Typography align="center" padding={10}>
                            Not found Document
                        </Typography>
                    </Box>
                ) : loadingContent ? (
                    <Box>
                        <Typography align="center" padding={10}>
                            ..Loading
                        </Typography>
                    </Box>
                ) : !currentDocument ? (
                    <Box>
                        <Typography align="center" padding={10}>
                            Not found Content
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        <Markdown
                            children={currentDocument.content}
                            options={{
                                overrides: {
                                    img: {
                                        component: ResponsiveImage,
                                    },
                                },
                                forceBlock: true,
                            }}
                        />
                    </Box>
                )}
            </Grid>
        </Grid>
    )
}
